import * as React from 'react'
import {useLocation} from 'react-router-dom'
import {authenticator} from '../../../app/auth/SessionPlusAuthenticator'
import {arPathTrialAccount, isArPath} from '../../store/ar.paths'

const KIARA_OPEN_KEY = 'oegbv/kiara-open'

// Kiara starts open, so users who have never touched the toggle see it right away.
const getPersistentShowKiara = (): boolean => window.localStorage.getItem(KIARA_OPEN_KEY) !== 'false'

const setPersistentShowKiara = (show: boolean) => {
  window.localStorage.setItem(KIARA_OPEN_KEY, show ? 'true' : 'false')
}

// Kiara belongs to the Arbeitsrecht area, but it is not part of what the trial account page offers.
const isKiaraPath = (pathname: string): boolean => isArPath(pathname) && !pathname.startsWith(arPathTrialAccount())

export interface KiaraContextValue {
  kiaraEnabled: boolean
  showKiara: boolean
  toggleKiara: () => void
  closeKiara: () => void
}

export const KiaraContext = React.createContext<KiaraContextValue>({
  kiaraEnabled: false,
  showKiara: false,
  toggleKiara: () => undefined,
  closeKiara: () => undefined,
})

export const useKiara = (): KiaraContextValue => React.useContext(KiaraContext)

export const KiaraProvider: React.FC = ({children}) => {
  const {pathname} = useLocation()
  const [showKiara, setShowKiara] = React.useState(getPersistentShowKiara())

  const kiaraEnabled = isKiaraPath(pathname) && authenticator.isAuthenticated() && authenticator.hasArfbr()

  const updateShowKiara = React.useCallback((show: boolean) => {
    setShowKiara(show)
    setPersistentShowKiara(show)
  }, [])

  const toggleKiara = React.useCallback(() => updateShowKiara(!showKiara), [showKiara, updateShowKiara])
  const closeKiara = React.useCallback(() => updateShowKiara(false), [updateShowKiara])

  const value: KiaraContextValue = {kiaraEnabled, showKiara, toggleKiara, closeKiara}

  return <KiaraContext.Provider value={value}>{children}</KiaraContext.Provider>
}
